import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, throwError } from 'rxjs';
import { JobFile } from '@core/models/job-file/job-file.model';
import { JobFileService } from '@core/services/job-file.service';
import { AuthState } from '@core/store/auth/auth.state';
import { ServiceRequest } from '../models/service-request.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { API } from '@configs/api.config';
import { CollaboratorsInvitationModel } from 'app/modules/wizard/models/collaborators-invitation.model';
import { Organization } from 'app/modules/organization/models/organization';
import { Pageable } from '@shared/models/pageable.model';
import { map } from 'rxjs/operators';
import { JobFileWithLoadAssignmentDetails } from '@core/models/job-file/job-file-with-load-assignment-details.model';
import { CreateRFQDto, SpotRFQResponse } from '../models/create-rfq.model';
import { GraphqlClientService } from '@core/services/graphql-client.service';
import { updateSelectedLoadMastersStatus } from '../queries/load-master.queries';

/** Apollo client name for the ms-report GraphQL endpoint, as registered in GraphqlClientService. */
const REPORT_ENDPOINT = 'report-endpoint';

@Injectable({
  providedIn: 'root'
})
export class LoadAssignmentService {

  constructor(
    private readonly jobFileService: JobFileService,
    private readonly store: Store,
    private httpClient: HttpClient,
    private readonly gqlService: GraphqlClientService
  ) {}

  getJobFile(jobRefId: string): Observable<JobFile> {
    const orgId = this.store.selectSnapshot(AuthState.getOrgId);

    if (!orgId) {
      return throwError(() => new Error('Organization id not found'));
    }
    return this.jobFileService.getJobFileByOrgIdAndJobRefId(orgId, jobRefId);
  }

  getJobFileWithLoadAssignmentDetails(jobRefId: string) {
    const orgId = this.store.selectSnapshot(AuthState.getOrgId);
    if (!orgId) {
      return throwError(() => new Error('Organization id not found'));
    }
    return this.jobFileService.getJobFileForLoadAssignmentPage(orgId, jobRefId);
  }

  removeLoadReferenceForContainer(jobRefId: string, containerId: string): Observable<void> {
    const url = API.jobFile.removeLoadReferenceForContainer.format([jobRefId, containerId]);
    return this.httpClient.delete<void>(url);
  }

  getTenantKeyByOrgId(orgId: string): Observable<string> {
    return this.httpClient.get(API.organization.getTenantKeyByOrgId.format([orgId]), { responseType: 'text' }).pipe(
      map(response => (response ? response.trim() : ''))
    );
  }

  unassignResourcesForContainer(tenantKey: string, orgId: string, jobRefId: string, containerId: string): Observable<unknown> {
    const url = API.schedule.unassignResourcesForContainer.format([tenantKey, orgId, jobRefId, containerId]);
    return this.httpClient.post(url, {});
  }

  sendServiceRequest(serviceRequest: ServiceRequest): Observable<ServiceRequest> {
    const url = API.serviceRequests.createServiceRequest;
    return this.httpClient.post<ServiceRequest>(url, serviceRequest);
  }

  inviteCollaborators(collaboratorsInvitationModel: CollaboratorsInvitationModel): Observable<void> {
    const url = API.jobFile.inviteCollaborators;
    return this.httpClient.post<void>(url, collaboratorsInvitationModel);
  }

  calculateBulkByRateCardIds(items: { containerId: string; rateCardId: string; attributes?: any; rateBlockKey?: string | null }[], gpMarkup: number = 0): Observable<any> {
    const url = API.serviceRequests.calculateBulkByRateCardIds;
    const payload = {
      gpMarkup,
      items
    };
    return this.httpClient.post<any>(url, payload);
  }

  createSpotRFQ(dto: CreateRFQDto): Observable<SpotRFQResponse> {
    const url = API.spotRFQs.createSpotRFQ;
    return this.httpClient.post<SpotRFQResponse>(url, dto);
  }

  getOnBoardedOrganizationsByVertical(vertical: string, searchKey: string = '', pageNumber: number = 0, pageSize: number = 20): Observable<Organization[]> {
    const url = API.organization.getOnBoardedOrganizationsByVertical.format([vertical]);
    let params = new HttpParams();
    if (searchKey && searchKey.trim() !== '') {
      params = params.append('searchKey', searchKey);
    }
    if (pageNumber !== undefined && pageSize !== undefined) {
      params = params.append('pageNumber', pageNumber.toString());
      params = params.append('pageSize', pageSize.toString());
    }
    return this.httpClient.get<Pageable<Organization> | Organization[]>(url, { params }).pipe(
      map(response => {
        if (Array.isArray(response)) {
          return response;
        } else if (response && 'content' in response) {
          return response.content || [];
        }
        return [];
      }),
    );
  }

  /**
   * Set the load-master status for a batch of loads in ms-report.
   * `containerIds` are load-master containerIds (the loadIds shown on the load-assignment table).
   */
  updateLoadMastersStatus(jobRefId: string, containerIds: string[], orgId: string, status: string): Observable<string> {
    return this.gqlService.mutateDataFromGQL<string>(
      { jobRefId, containerIds, orgId, status },
      updateSelectedLoadMastersStatus,
      'updateSelectedLoadMastersStatus',
      REPORT_ENDPOINT
    );
  }

  saveLoadQuotationOnBehalfOf(
    rootJobOrgId: string,
    serviceProviderOrgId: string,
    customerId: string,
    jobRefId: string,
    rootJobRefId: string,
    containerId: string,
    loadRateCardData: any
  ): Observable<any> {
    const url = API.quotation.saveLoadQuotationOnBehalfOf.format([
      rootJobOrgId, serviceProviderOrgId, customerId, jobRefId, rootJobRefId, containerId
    ]);
    return this.httpClient.post(url, loadRateCardData);
  }
}

